import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Users2, CalendarCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth } from "date-fns";

const fm = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Payroll() {
  const [tab, setTab] = useState("employees");
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">People</div>
        <h1 className="font-display text-4xl mt-2">Payroll</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
          Employees and pay runs. Approve a run to auto-post the payroll journal to your books.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[var(--hover)]">
          <TabsTrigger value="employees" data-testid="tab-employees"><Users2 className="w-4 h-4 mr-2" /> Employees</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs"><CalendarCheck className="w-4 h-4 mr-2" /> Pay runs</TabsTrigger>
        </TabsList>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
        <TabsContent value="runs"><RunsTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

function EmployeesTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  function blank() { return { name: "", email: "", role: "", hire_date: format(new Date(), "yyyy-MM-dd"), gross_salary: 0, pay_frequency: "monthly", tax_rate: 0, notes: "" }; }
  const load = async () => { const { data } = await api.get("/employees"); setRows(data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { ...form, gross_salary: parseFloat(form.gross_salary) || 0, tax_rate: parseFloat(form.tax_rate) || 0 };
    try {
      if (editing) await api.patch(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="emp-new-btn" onClick={() => { setEditing(null); setForm(blank()); setOpen(true); }} className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit employee" : "New employee"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="emp-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
              <div><Label>Hire date</Label><Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></div>
              <div><Label>Annual gross</Label><Input type="number" step="0.01" data-testid="emp-form-salary" value={form.gross_salary} onChange={(e) => setForm({ ...form, gross_salary: e.target.value })} /></div>
              <div><Label>Pay frequency</Label>
                <Select value={form.pay_frequency} onValueChange={(v) => setForm({ ...form, pay_frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tax rate (%)</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="emp-form-save" onClick={save} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-flat overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">No employees yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Name</th><th className="text-left px-4 py-2.5">Role</th><th className="px-4 py-2.5">Frequency</th><th className="text-right px-4 py-2.5">Annual gross</th><th className="text-right px-4 py-2.5">Tax %</th><th className="w-24" /></tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-3 font-medium">{e.name}</td>
                  <td className="px-4 py-3 text-[var(--muted-text)]">{e.role || "—"}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest text-[var(--muted-text)]">{e.pay_frequency}</td>
                  <td className="px-4 py-3 text-right font-mono">{fm(e.gross_salary)}</td>
                  <td className="px-4 py-3 text-right font-mono">{e.tax_rate}%</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(e); setForm({ ...e }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function RunsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ period_start: format(startOfMonth(new Date()), "yyyy-MM-dd"), period_end: format(endOfMonth(new Date()), "yyyy-MM-dd") });
  const load = async () => { const { data } = await api.get("/pay-runs"); setRows(data); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/pay-runs", form); toast.success("Pay run drafted"); setOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const approve = async (id) => {
    try {
      const { data } = await api.post(`/pay-runs/${id}/approve`);
      toast.success("Approved & journal posted");
      load();
    } catch (err) { toast.error("Approval failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this pay run? Any auto-posted journal will be reversed.")) return;
    try { await api.delete(`/pay-runs/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="run-new-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-10 px-4"><Plus className="w-4 h-4 mr-2" /> New pay run</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create pay run</DialogTitle><DialogDescription>All active employees will be included at their configured frequency.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Period start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} /></div>
              <div><Label>Period end</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="run-form-save" onClick={create} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {rows.length === 0 ? (
          <div className="card-flat p-10 text-center text-sm text-[var(--muted-text)]">No pay runs yet.</div>
        ) : rows.map((r) => (
          <div key={r.id} data-testid={`run-row-${r.id}`} className="card-flat p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm font-medium">{r.period_start} → {r.period_end}</div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${r.status === "approved" ? "bg-[#E5F1E9] text-[#2F6B4A]" : "bg-[var(--hover)] text-[var(--muted-text)]"}`}>{r.status}</span>
              <div className="flex-1" />
              <div className="text-sm">
                <span className="text-[var(--muted-text)] mr-2">Gross</span><span className="font-mono">{fm(r.totals.gross)}</span>
                <span className="text-[var(--muted-text)] mx-2">Net</span><span className="font-mono">{fm(r.totals.net)}</span>
              </div>
              {r.status !== "approved" && (
                <Button size="sm" data-testid={`run-approve-${r.id}`} onClick={() => approve(r.id)} className="rounded-full bg-[#2F6B4A] hover:bg-[#245536] text-white">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & post
                </Button>
              )}
              <Button size="icon" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
            </div>
            {r.lines.length > 0 && (
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]"><th className="text-left py-1.5">Employee</th><th className="text-right py-1.5">Gross</th><th className="text-right py-1.5">Tax</th><th className="text-right py-1.5">Net</th></tr>
                </thead>
                <tbody>
                  {r.lines.map((l) => (
                    <tr key={`${r.id}-${l.employee_id || l.employee_name}`} className="border-b border-[var(--divider)] last:border-none">
                      <td className="py-1.5">{l.employee_name}</td>
                      <td className="py-1.5 text-right font-mono">{fm(l.gross)}</td>
                      <td className="py-1.5 text-right font-mono text-[#B4283A]">{fm(l.tax)}</td>
                      <td className="py-1.5 text-right font-mono">{fm(l.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
